import { toast } from 'react-toastify';

import { confirmPopup } from 'components/Popups/Confirm';
import { TotemSelect } from 'components/TotemSelect';
import { TourState, useUpdateTourStateMutation } from 'data/__generated__';

import { tourStateLabel, tourStateOptions } from '../helpers';
import type { DetailTour } from './helpers';

const STATE_OPTIONS = tourStateOptions();

/**
 * Correction of the driving state, in BOTH directions — the driver's app only moves it forward, so a tour
 * started by mistake or left « En cours » at nightfall is only repairable from here. Every correction is
 * traced in the history, and moving back below « En cours » makes the tour composable again on `/tours`.
 */
export const useTourStateUpdate = ({
    tour,
    pendingState,
    onApplied,
}: {
    tour?: DetailTour;
    pendingState: TourState | null;
    onApplied: () => void;
}) => {
    const [updateTourState, { loading }] = useUpdateTourStateMutation();

    const applyState = async (): Promise<void> => {
        if (!tour || !pendingState || pendingState === tour.state) return;

        const label = tourStateLabel(pendingState);
        const hasConfirmed = await confirmPopup({
            title: 'Confirmer',
            message: `Forcer la tournée ${tour.name} à « ${label} » ?`,
        });
        if (!hasConfirmed) return;

        try {
            const { data } = await updateTourState({ variables: { _id: tour._id, state: pendingState } });
            const refusal = data?.updateTourState.error;
            if (refusal) {
                toast.error(`Changement d'état refusé : ${refusal}`);
                return;
            }
            toast.success(`Tournée ${tour.name} passée à « ${label} »`);
            onApplied();
        } catch (error) {
            toast.error(`Erreur de changement d'état : ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    return { applyState, loading, isDirty: Boolean(tour && pendingState && pendingState !== tour.state) };
};

export const StateSelect = ({ value, onChange }: { value: TourState; onChange: (state: TourState | null) => void }) => (
    <TotemSelect<TourState> label="Statut" value={value} options={STATE_OPTIONS} onChange={onChange} />
);
